import {isArray, isEmpty, size} from 'lodash';
import PubNub from 'pubnub';
import System from '../api/system';
import Logger from './serverLogger.service';

const Message = {};
Message.pubnub = null;
Message.socket = null;
Message.useWebsockets = false;
Message.channels = new Set();
Message.listenerMap = {};

async function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForConnection(socket) {
  if (socket.readyState === WebSocket.OPEN) return Promise.resolve();
  for (let i = 0; i < 100; ++i) {
    await sleep(100);
    if (socket.readyState === WebSocket.OPEN) return Promise.resolve();
  }
  throw new Error('Websocket never connected.');
}

async function reconnect() {
  // eslint-disable-next-line no-use-before-define
  await connect();
  if (Message.channels && Message.channels.size) {
    const channels = [...Message.channels];
    console.log(`Reconnecting to the follwing channels: ${channels}`);
    await Message.subscribe(channels);
  }
  if (!isEmpty(Message.listenerMap)) {
    console.log(`Re-adding ${size(Message.listenerMap)} listeners`);
    for (const [key, value] of Object.entries(Message.listenerMap)) {
      console.log(`Re-adding ${key}`);
      await Message.addListener(value, key);
    }
  }
}

async function onDisconnect() {
  console.log(`Websocket disconnected. Reconnecting at ${new Date()}`);
  return reconnect();
}

async function onError(error) {
  console.error('Websocket error:');
  console.error(error);
  await sleep(5000);
  return reconnect();
}

async function connect() {
  Message.useWebsockets = true;
  Message.socket = new WebSocket(Message.url);
  Message.socket.onclose = onDisconnect;
  Message.socket.onerror = onError;
  await waitForConnection(Message.socket); // wait for socket to open
}

Message.setup = async () => {
  if (Message.pubnub) return Message.pubnub;
  if (Message.socket) return Message.socket;

  try {
    const config = await System.getMessageConfig();
    if (config.type === 'pubnub') {
      Message.pubnub = new PubNub({
        subscribeKey: config.key
      });
      Message.useWebsockets = false;
    }
    if (config.type === 'ws') {
      Message.useWebsockets = true;
      Message.url = config.url;
      await connect();
    }
  } catch (error) {
    console.error('Unable to init messaging:');
    console.error(error);
    Logger.error(error);
  }
};

Message.addChannel = async (channels) => Message.subscribe(channels);

Message.removeChannel = async (channels) => Message.unsubscribe(channels);

Message.addListener = async (listener, listenerKey) => {
  if (!listener) return;
  if (Message.useWebsockets) {
    if (!listenerKey) throw new Error('Must provide listenerKey when using websockets');
    const getMessage = (event) => {
      const data = JSON.parse(event.data);
      listener.message(data);
    };
    Message.socket.addEventListener('message', getMessage);
    Message.listenerMap[listenerKey] = listener;
    console.log(`Setting listener for ${listenerKey}`);
  } else {
    Message.pubnub.addListener(listener);
  }
};

async function pnSubscribe(channelList, listener) {
  console.log(`Subscribing: ${channelList}`);
  await Message.addListener(listener);
  await Message.pubnub.subscribe({channels: channelList, timetoken: '0'}); // timetoken here is important because the NPM package seems to not be sending a query param of tt=0 for the start and pubnub then sends old messages that we don't want, adding this tells it we don't want any messages from the past
}

async function wsSubscribe(channelList, listener, listenerKey) {
  console.log(`Subscribing Websocket: ${channelList}`);
  await waitForConnection(Message.socket);
  const smsg = JSON.stringify({action: 'subscribeChannel', channel: channelList});
  await Message.addListener(listener, listenerKey);
  await Message.socket.send(smsg);
}

Message.subscribe = async (channels, listener, listenerKey) => {
  const channelList = isArray(channels) ? channels : [channels];
  channelList.forEach((item) => Message.channels.add(item));
  if (!Message.useWebsockets) return pnSubscribe(channelList, listener);
  return wsSubscribe(channelList, listener, listenerKey);
};

async function pnUnsubscribe(channelList, listener) {
  console.log(`Unsubscribing: ${channelList}`);
  Message.pubnub.removeListener(listener);
  Message.pubnub.unsubscribe({channels: channelList});
}

async function wsUnsubscribe(channelList, listener, listenerKey) {
  console.log(`Unsubscribing WS: ${channelList}`);
  await waitForConnection(Message.socket);
  const smsg = JSON.stringify({action: 'unsubscribeChannel', channel: channelList});
  await Message.socket.send(smsg);
  if (listener && listenerKey) {
    Message.socket.removeEventListener('message', Message.listenerMap[listenerKey]);
    delete Message.listenerMap[listenerKey];
  }
}

Message.unsubscribe = (channels, listener, listenerKey) => {
  const channelList = isArray(channels) ? channels : [channels];
  channelList.forEach((item) => Message.channels.delete(item));
  if (!Message.useWebsockets) return pnUnsubscribe(channelList, listener);
  return wsUnsubscribe(channelList, listener, listenerKey);
};

Message.teardown = () => {
  if (Message.pubnub) Message.pubnub.unsubscribeAll();
  // Note: the WebSockets unsubscribe on disconnect "server"-side
};

export default Message;
